import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

export interface CompanyProfileOffering {
  /** Text shown on the chip; for "Bedroom Furniture (beds, wardrobes)" this is "Bedroom Furniture". */
  name: string;
  /** Bracketed detail split from the name when splitDetails is on, otherwise ''. */
  detail: string;
  /** The full display text, used for the chip title. */
  label: string;
}

export type CompanyProfileOfferingsLayout = 'stack' | 'wrap';

const NO_ITEMS: string[] = [];
const itemsByOverview = new WeakMap<object, string[]>();

/**
 * The raw offerings for a company: products_services from the LLM summary, falling back to website keywords.
 * The result is cached per overview object so template getters hand the component the same array on every check.
 */
export function companyProfileOfferingItems(overview: Record<string, any> | null | undefined): string[] {
  if (!overview || typeof overview !== 'object') return NO_ITEMS;
  let cached = itemsByOverview.get(overview);
  if (!cached) {
    const items = overview.products_services?.length ? overview.products_services : overview.keywords;
    cached = Array.isArray(items) ? items.filter((item): item is string => typeof item === 'string' && !!item.trim()) : [];
    itemsByOverview.set(overview, cached);
  }
  return cached;
}

/**
 * Prepares company_overview.products_services (or keywords) for display: keeps non-empty strings, drops any
 * leading "Segment - " prefix, and removes case-insensitive duplicates while keeping the order the data gives.
 */
export function companyProfileOfferings(items: unknown, splitDetails = false): CompanyProfileOffering[] {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  const offerings: CompanyProfileOffering[] = [];
  for (const item of items) {
    if (typeof item !== 'string' || !item.trim()) continue;
    const label = (item.split(' - ').pop() || item).trim();
    const key = label.toLowerCase().replace(/\s+/g, ' ');
    if (!label || seen.has(key)) continue;
    seen.add(key);
    const match = splitDetails ? label.match(/^(.+?)\s*\(([^()]+)\)$/) : null;
    offerings.push({ name: match ? match[1] : label, detail: match ? match[2].trim() : '', label });
  }
  return offerings;
}

// Shared products and services block for the skinned company profiles.
// 'stack' is the Logistics hero column (one chip per line, scrolls inside a fixed height);
// 'wrap' lays chips out in rows for a full-width band. Colours come from the --profile-offerings-* properties.
@Component({
  selector: 'company-profile-offerings',
  templateUrl: './company-profile-offerings.component.html',
  styleUrls: ['./company-profile-offerings.component.css'],
  standalone: false,
})
export class CompanyProfileOfferingsComponent implements OnChanges {
  @Input() items: unknown = [];
  @Input() heading = 'Products & services';
  @Input() headingId = 'company-profile-offerings-title';
  @Input() listId = 'company-profile-all-offerings';
  @Input() layout: CompanyProfileOfferingsLayout = 'stack';
  /** How many chips show before the expand control appears. */
  @Input() previewCount = 2;
  @Input() expandLabel = 'View all';
  @Input() collapseLabel = 'Show fewer';
  @Input() showCount = false;
  /** Optional icon before the heading, e.g. 'fal fa-box-open'. */
  @Input() iconClass = '';
  /** Show a trailing "(...)" as lighter detail text after the chip name. */
  @Input() splitDetails = false;
  /** When false the heading still renders with no items, for skins that add their own content beneath. */
  @Input() hideWhenEmpty = true;

  offerings: CompanyProfileOffering[] = [];
  expanded = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['splitDetails']) {
      this.offerings = companyProfileOfferings(this.items, this.splitDetails);
      if (changes['items']) this.expanded = false;
    }
  }

  get canExpand(): boolean {
    return this.offerings.length > this.previewCount;
  }

  get visibleOfferings(): CompanyProfileOffering[] {
    return this.expanded || !this.canExpand ? this.offerings : this.offerings.slice(0, this.previewCount);
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }
}
